import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { Heart, ArrowLeft, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  FAVORITE_CATEGORIES,
  FAVORITE_GROUP_META,
  readFavorites,
  writeFavorites,
  type FavoriteGroup,
  type FavoriteRecord,
} from "@/lib/favorites";

export const Route = createFileRoute("/favorites")({
  head: () => ({
    meta: [
      { title: "My Favourites — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Everything you've saved across Recovery Trauma™ — tools, articles, and affirmation cards — in one gentle place.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: withMemberGate(FavoritesPage, "Favourites"),
});

type CategoryItems = { id: string; items: FavoriteRecord[] };

const GROUP_ORDER: FavoriteGroup[] = ["tools", "articles", "cards", "other"];

function FavoritesPage() {
  const [data, setData] = useState<CategoryItems[]>([]);
  const [filter, setFilter] = useState<"all" | FavoriteGroup>("all");

  useEffect(() => {
    const refresh = () => {
      setData(
        FAVORITE_CATEGORIES.map((c) => ({
          id: c.id,
          items: readFavorites(c.key),
        })),
      );
    };
    refresh();
    // Pull latest server-side favourites for the signed-in member (idempotent).
    void import("@/lib/favorites-sync")
      .then((m) => m.syncFavoritesFromServer())
      .then(refresh)
      .catch(() => {});
    const handler = () => refresh();
    window.addEventListener("storage", handler);
    window.addEventListener("rt-favorites-change", handler as EventListener);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("rt-favorites-change", handler as EventListener);
    };
  }, []);

  const total = data.reduce((sum, c) => sum + c.items.length, 0);

  const groupCounts: Record<FavoriteGroup, number> = {
    tools: 0,
    articles: 0,
    cards: 0,
    other: 0,
  };
  for (const cat of FAVORITE_CATEGORIES) {
    const items = data.find((c) => c.id === cat.id)?.items ?? [];
    groupCounts[cat.group] += items.length;
  }

  const removeItem = (catKey: string, itemId: string) => {
    const next = readFavorites(catKey).filter((r) => r.id !== itemId);
    writeFavorites(catKey, next);
    void import("@/lib/favorites-sync")
      .then((m) => m.remoteDeleteFavorite(catKey, itemId))
      .catch(() => {});
  };

  const clearCategory = (catKey: string) => {
    writeFavorites(catKey, []);
    void import("@/lib/favorites-sync")
      .then((m) => m.remoteClearCategory(catKey))
      .catch(() => {});
  };


  return (
    <div className="min-h-dvh pb-20 pt-6">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Link
          to="/my-membership"
          className="mb-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> My Membership
        </Link>

        <header className="mb-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-2xl shadow-sm ring-1 ring-rose-200">
            <Heart className="h-5 w-5 fill-rose-500 text-rose-500" />
          </div>
          <h1 className="font-serif text-3xl text-foreground sm:text-4xl">
            My Favourites
          </h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {total === 0
              ? "Tap the heart icon anywhere in the app to save things here."
              : `${total} saved item${total === 1 ? "" : "s"} across your recovery journey.`}
          </p>
        </header>

        {total > 0 && (
          <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
            <FilterChip
              active={filter === "all"}
              onClick={() => setFilter("all")}
              label={`All (${total})`}
            />
            {GROUP_ORDER.map((g) => (
              <FilterChip
                key={g}
                active={filter === g}
                onClick={() => setFilter(g)}
                label={`${FAVORITE_GROUP_META[g].emoji} ${FAVORITE_GROUP_META[g].label} (${groupCounts[g]})`}
                disabled={groupCounts[g] === 0}
              />
            ))}
          </div>
        )}

        {total === 0 && (
          <div className="rounded-2xl border border-dashed border-gold/30 bg-cream/30 p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Nothing saved yet. Start by exploring{" "}
              <Link to="/" className="text-[var(--gold-deep)] underline-offset-2 hover:underline">
                your recovery journey
              </Link>
              .
            </p>
          </div>
        )}

        <div className="space-y-10">
          {GROUP_ORDER.filter((g) => filter === "all" || filter === g).map((group) => {
            const cats = FAVORITE_CATEGORIES.filter((c) => c.group === group);
            const hasItems = cats.some(
              (c) => (data.find((d) => d.id === c.id)?.items.length ?? 0) > 0,
            );
            if (!hasItems) return null;
            const meta = FAVORITE_GROUP_META[group];
            return (
              <div key={group}>
                <div className="mb-3 flex items-baseline gap-2 px-1">
                  <span className="text-lg" aria-hidden>{meta.emoji}</span>
                  <h2 className="font-serif text-xl text-foreground">{meta.label}</h2>
                  <span className="text-xs text-muted-foreground">
                    {meta.description}
                  </span>
                </div>
                <div className="space-y-4">
                  {cats.map((cat) => {
                    const items = data.find((c) => c.id === cat.id)?.items ?? [];
                    if (items.length === 0) return null;
                    return (
                      <section
                        key={cat.id}
                        className="overflow-hidden rounded-2xl border border-gold/20 bg-card/60"
                      >
                        <header className="flex items-center justify-between gap-2 border-b border-gold/10 px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl" aria-hidden>{cat.emoji}</span>
                            <h3 className="font-serif text-base text-foreground">{cat.label}</h3>
                            <span className="rounded-full bg-cream/60 px-2 py-0.5 text-[10px] font-medium text-[var(--gold-deep)]">
                              {items.length}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <Link
                              to={cat.browseTo}
                              className="text-xs text-[var(--gold-deep)] underline-offset-2 hover:underline"
                            >
                              Browse all
                            </Link>
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Remove all ${items.length} saved ${cat.label.toLowerCase()}?`,
                                  )
                                ) {
                                  clearCategory(cat.key);
                                }
                              }}
                              className="text-xs text-muted-foreground hover:text-destructive"
                            >
                              Clear
                            </button>
                          </div>
                        </header>
                        <ul className="divide-y divide-gold/10">
                          {items.map((item) => {
                            const to = item.to ?? cat.itemTo?.(item.id) ?? cat.browseTo;
                            return (
                              <li
                                key={`${cat.id}:${item.id}`}
                                className="flex items-center justify-between gap-3 px-5 py-3"
                              >
                                <Link
                                  to={to}
                                  className="flex-1 truncate text-sm text-foreground hover:text-[var(--gold-deep)]"
                                >
                                  <span className="block truncate font-medium">
                                    {item.label ?? item.id}
                                  </span>
                                  {item.sub && (
                                    <span className="block truncate text-[11px] text-muted-foreground">
                                      {item.sub}
                                    </span>
                                  )}
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => removeItem(cat.key, item.id)}
                                  aria-label={`Remove ${item.label ?? item.id}`}
                                  className="rounded-full p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full border px-3 py-1 text-xs transition ${
        active
          ? "border-[var(--gold-deep)] bg-[var(--gold-deep)] text-white"
          : "border-gold/30 bg-card/60 text-foreground hover:border-gold/60"
      } ${disabled ? "cursor-not-allowed opacity-40" : ""}`}
    >
      {label}
    </button>
  );
}
